/**
 * PII redaction for structured log payloads.
 *
 * Sanitizes log entries before they reach Axiom (via SDK transport or
 * Vercel log drain), reducing PII surface area across all environments.
 *
 * Complements the existing Sentry PII scrubbing (`sendDefaultPii: false`
 * + `beforeSend` hook) and the agent-proxy secret scrubber
 * (`src/app/api/agent-proxy/_lib/scrub.ts`).
 */

/**
 * Note: this regex uses the /g flag and is stored as a module-level
 * constant. Regex objects with /g are stateful (they track lastIndex),
 * but String.prototype.replace always resets lastIndex before matching,
 * so reuse across calls is safe.
 */
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g

/** Field names whose values are always redacted (case-insensitive match). */
const PII_FIELD_NAMES = new Set([
  'email',
  'name',
  'first_name',
  'firstname',
  'last_name',
  'lastname',
  'phone',
  'address',
  'ssn',
  'dob',
  'date_of_birth',
])

/**
 * Operational field names whose structural values pass through without
 * truncation. Email masking is still applied to string values — these
 * fields are "safe from redaction" but not "safe from email scrubbing",
 * since error messages and similar free-form text frequently contain PII.
 */
const OPERATIONAL_FIELDS = new Set([
  'tenantid',
  'tenant_id',
  'sobjectname',
  'level',
  'timestamp',
  '_time',
  'tag',
  'stage',
  'event',
  'service',
  'source',
  'message',
  'envelope_id',
  'envelopeid',
  'run_id',
  'runid',
  'invocation_id',
  'invocationid',
  'sync_object_config_id',
  'bulk_job_id',
  'job_id',
  'locator',
  'run_type',
  'runtype',
])

const MAX_VALUE_LENGTH = 500
const MAX_DEPTH = 5

const REDACTED = '[REDACTED]'
const EMAIL_REDACTED = '[EMAIL_REDACTED]'
const TRUNCATED_SUFFIX = '...[TRUNCATED]'
const MAX_DEPTH_SENTINEL = '[MAX_DEPTH_REACHED]'

/**
 * Mask email addresses found within a string value.
 * Returns the original string if no emails are found.
 */
function maskEmails(value: string): string {
  return value.replace(EMAIL_REGEX, EMAIL_REDACTED)
}

/**
 * Truncate a string value if it exceeds the maximum length.
 */
function truncate(value: string): string {
  if (value.length > MAX_VALUE_LENGTH) {
    return value.slice(0, MAX_VALUE_LENGTH) + TRUNCATED_SUFFIX
  }
  return value
}

/**
 * Apply email masking and truncation to a bare string value.
 * Used for string values that don't have a key context (e.g. array elements).
 */
function sanitizeString(value: string): string {
  return truncate(maskEmails(value))
}

/**
 * Sanitize a single value based on its type and the field key.
 */
function sanitizeValue(key: string, value: unknown, depth: number): unknown {
  // Redact known PII field names regardless of value type
  const keyLower = key.toLowerCase()
  if (PII_FIELD_NAMES.has(keyLower)) {
    return REDACTED
  }

  if (typeof value === 'string') {
    // Operational fields skip truncation but still get email masking,
    // since free-form text (e.g. error messages) often contains PII.
    if (OPERATIONAL_FIELDS.has(keyLower)) {
      return maskEmails(value)
    }
    return sanitizeString(value)
  }

  if (Array.isArray(value)) {
    return sanitizeArray(value, depth)
  }

  if (value !== null && typeof value === 'object') {
    return sanitizeObject(value as Record<string, unknown>, depth)
  }

  // Numbers, booleans, null, undefined pass through unchanged
  return value
}

/**
 * Recursively sanitize an array, applying the same rules to each element.
 */
function sanitizeArray(arr: unknown[], depth: number): unknown[] {
  if (depth >= MAX_DEPTH) return [MAX_DEPTH_SENTINEL]
  return arr.map((item) => {
    if (typeof item === 'string') {
      return sanitizeString(item)
    }
    if (Array.isArray(item)) {
      return sanitizeArray(item, depth + 1)
    }
    if (item !== null && typeof item === 'object') {
      return sanitizeObject(item as Record<string, unknown>, depth + 1)
    }
    return item
  })
}

/**
 * Recursively sanitize a plain object, applying redaction rules to each field.
 */
function sanitizeObject(
  obj: Record<string, unknown>,
  depth: number,
): Record<string, unknown> {
  if (depth >= MAX_DEPTH) return { _sanitized: MAX_DEPTH_SENTINEL }

  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    result[key] = sanitizeValue(key, value, depth + 1)
  }
  return result
}

/**
 * Sanitize a structured log payload before it reaches Axiom.
 *
 * Rules applied (in order):
 * 1. Known PII field names (email, name, phone, etc.) have their values replaced with `[REDACTED]`
 * 2. Email patterns in string values are replaced with `[EMAIL_REDACTED]` (including operational fields)
 * 3. Non-operational string values over 500 characters are truncated with `...[TRUNCATED]`
 * 4. Nested objects are recursively sanitized (max depth 5); deeper subtrees are replaced with a safe sentinel
 * 5. Operational fields (tenantId, stage, event, etc.) skip truncation but still get email masking
 * 6. Non-string primitives (numbers, booleans) pass through unchanged
 */
export function sanitize(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  return sanitizeObject(payload, 0)
}
